import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const PartnerCard = ({ partner }) => {
  const inner = (
    <>
      <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-4 h-32 w-full flex items-center justify-center">
        {partner.logo_url ? (
          <img
            src={partner.logo_url}
            alt={`Logo ${partner.name}`}
            className="max-h-full max-w-full w-auto h-auto object-contain"
            loading="lazy"
          />
        ) : (
          <span className="text-5xl font-bold text-gray-300" aria-hidden="true">
            {partner.name.charAt(0)}
          </span>
        )}
      </div>
      <p className="text-center text-sm text-gray-700 mt-3 font-medium truncate px-2">
        {partner.name}
      </p>
    </>
  );

  return (
    <div
      className="shrink-0 w-56 mx-3"
      data-testid={`partner-card-${partner.id}`}
    >
      {partner.website ? (
        <a
          href={partner.website}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visitar site de ${partner.name}`}
          className="block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-xl"
          data-testid={`partner-link-${partner.id}`}
        >
          {inner}
        </a>
      ) : (
        <div>{inner}</div>
      )}
    </div>
  );
};

const MarqueeRow = ({ partners, reverse = false, duration = 45 }) => {
  if (!partners.length) return null;

  // Repeat the array enough times so the row is wide enough for a smooth loop
  const minItems = 8;
  const repeats = Math.max(1, Math.ceil(minItems / partners.length));
  const set = Array.from({ length: repeats }, () => partners).flat();

  return (
    <div
      className="marquee-pause overflow-hidden py-2"
      style={{ '--marquee-duration': `${duration}s` }}
      data-testid={reverse ? 'partners-marquee-row-2' : 'partners-marquee-row-1'}
    >
      <div className={`flex w-max ${reverse ? 'animate-marquee-reverse' : 'animate-marquee'}`}>
        {[...set, ...set].map((p, i) => (
          <PartnerCard key={`${p.id}-${i}`} partner={p} />
        ))}
      </div>
    </div>
  );
};

const Partners = () => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPartners = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/partners/`);
      const partnersWithFullUrls = data.map((partner) => ({
        ...partner,
        logo_url: partner.logo_url?.startsWith('http')
          ? partner.logo_url
          : partner.logo_url
            ? `${API_URL}${partner.logo_url}`
            : '',
      }));
      setPartners(
        partnersWithFullUrls.sort((a, b) => a.display_order - b.display_order)
      );
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const useTwoRows = partners.length >= 12;
  const half = Math.ceil(partners.length / 2);
  const row1 = useTwoRows ? partners.slice(0, half) : partners;
  const row2 = useTwoRows ? partners.slice(half) : [];

  return (
    <section
      id="partners"
      className="py-20 bg-gray-50"
      aria-labelledby="partners-heading"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            id="partners-heading"
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('partners.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('partners.subtitle')}
          </p>
        </div>

        {/* Partners Marquee */}
        {loading ? (
          <div className="flex justify-center py-12" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="sr-only">Carregando parceiros</span>
          </div>
        ) : partners.length > 0 ? (
          <div
            className="relative space-y-2"
            data-testid="partners-marquee"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
            }}
          >
            <MarqueeRow partners={row1} duration={45} />
            {useTwoRows && <MarqueeRow partners={row2} reverse duration={55} />}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            {t('partners.noPartners') || 'Nenhum parceiro disponível no momento'}
          </div>
        )}

        {/* Image Section */}
        <div className="mt-16 relative rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://images.unsplash.com/photo-1759310610325-2c7cb621e5e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwfGVufDB8fHx8MTc3NTI1NDIxN3ww&ixlib=rb-4.1.0&q=85"
            alt="Parcerias e Colaborações"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-teal-900/80 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h3 className="text-3xl font-bold mb-4">
                {t('partners.bannerTitle')}
              </h3>
              <p className="text-lg mb-6 max-w-2xl">
                {t('partners.bannerText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Partners;
